import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Paper, Typography, Avatar, IconButton, TextField,
  Stack, Badge, Divider, CircularProgress, Tooltip, Button,
  InputBase, Container
} from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import SearchIcon from '@mui/icons-material/Search';
import SendIcon from '@mui/icons-material/Send';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import { sendMessage, getChats, setCurrentChat, deleteChat } from '../../actions/chat';
import { urlImg } from '../common/helpers';
import ModalCotizar from './ModalCotizar';
import ModalDetalleCotizar from './ModalDetalleCotizar';

// Nombre legible del tema de un chat (servicio/página) y su categoría de respaldo
const temaTitulo = (chat) => chat?.tema?.nombre || chat?.tema?.categoria || '';
const temaCategoria = (chat) => (chat?.tema?.nombre && chat?.tema?.categoria) ? chat.tema.categoria : '';

const ChatPage = () => {
  const dispatch = useDispatch();
  const { currentChat, messages, chats, loading, onlineUsers } = useSelector(state => state.chat);
  const authUser = useSelector(state => state.auth.user || state.user.user); // Check both potential locations

  const getStrId = (id) => {
    if (!id) return '';
    if (typeof id === 'string') return id;
    if (typeof id === 'object' && id._id) return id._id.toString();
    return String(id);
  };

  const otherParticipant = currentChat?.participants?.find(p => getStrId(p._id) !== getStrId(authUser?._id));
  const isOnline = otherParticipant && onlineUsers.some(id => getStrId(id) === getStrId(otherParticipant._id));

  const [text, setText] = useState('');
  const [search, setSearch] = useState('');
  const messagesEndRef = useRef(null);
  const [openCotizar, setOpenCotizar] = useState(false);
  const [openQuoteDetail, setOpenQuoteDetail] = useState(false);
  const [selectedQuoteId, setSelectedQuoteId] = useState(null);

  useEffect(() => {
    dispatch(getChats());
    dispatch({ type: 'TOGGLE_CHAT', payload: true }); // Asegurar que el estado isOpen sea true en pantalla completa

    return () => {
      dispatch({ type: 'TOGGLE_CHAT', payload: false }); // Limpiar al salir
    };
  }, [dispatch]);

  useEffect(() => {
    if (messages && messages.length > 0) {
      // Forzar scroll al fondo con timeout para asegurar que el DOM se actualizó
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }, 100);
    }
  }, [messages]);

  const handleSend = (e) => {
    e.preventDefault();
    if (text.trim() && currentChat) {
      dispatch(sendMessage(currentChat._id, text));
      setText('');
    }
  };

  // Safety check for chats array
  const safeChats = Array.isArray(chats) ? chats : [];

  const filteredChats = safeChats.filter(chat => {
    const other = chat.participants?.find(p => p._id !== authUser?._id);
    const fullName = `${other?.name || ''} ${other?.apellido || ''}`.toLowerCase();
    return fullName.includes(search.toLowerCase());
  });

  return (
    <Container maxWidth="xl" sx={{ mt: 15, mb: 2, height: 'calc(100vh - 200px)' }}>
      <Paper elevation={0} sx={{ height: '100%', borderRadius: '20px', overflow: 'hidden', border: '1px solid #E2E8F0', display: 'flex' }}>

        {/* Sidebar: Chat List */}
        <Box sx={{ width: { xs: '80px', md: '350px' }, borderRight: '1px solid #E2E8F0', display: 'flex', flexDirection: 'column', bgcolor: '#F8FAFC' }}>
          <Box sx={{ p: 3, display: { xs: 'none', md: 'block' } }}>
            <Typography variant="h5" sx={{ fontWeight: 800, color: '#0E2465', mb: 2 }}>Messages</Typography>
            <Paper
              elevation={0}
              sx={{
                p: '4px 12px', display: 'flex', alignItems: 'center', bgcolor: '#F1F5F9', borderRadius: '12px'
              }}
            >
              <SearchIcon sx={{ color: '#94A3B8', mr: 1 }} />
              <InputBase
                placeholder="Buscar"
                fullWidth
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{ fontSize: '0.9rem' }}
              />
            </Paper>
          </Box>

          <Box sx={{ flex: 1, overflowY: 'auto', px: { xs: 1, md: 2 } }}>
            {loading && safeChats.length === 0 ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress size={24} /></Box>
            ) : filteredChats.length > 0 ? (
              filteredChats.map(chat => {
                const other = chat.participants?.find(p => getStrId(p._id) !== getStrId(authUser?._id));
                const isActive = getStrId(currentChat?._id) === getStrId(chat._id);
                const avatar = other?.avatar ? (other.avatar.startsWith('http') ? other.avatar : `${urlImg}/users/${getStrId(other._id)}/${other.avatar}`) : '';
                const userIsOnline = other && onlineUsers.some(id => getStrId(id) === getStrId(other._id));

                return (
                  <Box
                    key={chat._id}
                    onClick={() => dispatch(setCurrentChat(chat))}
                    sx={{
                      p: 2, mb: 1, borderRadius: '16px', cursor: 'pointer',
                      display: 'flex', alignItems: 'center', gap: 2,
                      bgcolor: isActive ? '#FFFFFF' : 'transparent',
                      boxShadow: isActive ? '0 4px 12px rgba(0,0,0,0.05)' : 'none',
                      '&:hover': { bgcolor: isActive ? '#FFFFFF' : '#F1F5F9' },
                      transition: 'all 0.2s'
                    }}
                  >
                    <Badge color="success" variant="dot" invisible={!userIsOnline} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
                      <Avatar src={avatar} sx={{ width: 48, height: 48 }} />
                    </Badge>
                    <Box sx={{ flex: 1, minWidth: 0, display: { xs: 'none', md: 'block' } }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1E293B' }} noWrap>
                          {other?.name} {other?.apellido}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#94A3B8' }}>
                          {chat.lastMessage ? new Date(chat.lastMessage.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                        </Typography>
                      </Stack>
                      {temaTitulo(chat) && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, my: 0.25 }}>
                          <LocalOfferOutlinedIcon sx={{ fontSize: 13, color: '#7495F8', flexShrink: 0 }} />
                          <Typography variant="caption" noWrap sx={{ color: '#7495F8', fontWeight: 600 }}>
                            {temaTitulo(chat)}
                          </Typography>
                        </Box>
                      )}
                      <Typography variant="caption" noWrap sx={{ color: isActive ? '#0E2465' : '#64748B', display: 'block', fontWeight: chat.unreadCount?.[authUser?._id] > 0 ? 700 : 400 }}>
                        {chat.lastMessage?.content || 'Sin mensajes'}
                      </Typography>
                    </Box>
                    {chat.unreadCount?.[authUser?._id] > 0 && (
                      <Badge badgeContent={chat.unreadCount[authUser._id]} color="primary" sx={{ display: { xs: 'none', md: 'block' } }} />
                    )}
                  </Box>
                );
              })
            ) : (
              <Box sx={{ textAlign: 'center', mt: 4, opacity: 0.5, display: { xs: 'none', md: 'block' } }}>
                <Typography variant="body2">No hay conversaciones</Typography>
              </Box>
            )}
          </Box>
        </Box>

        {/* Main Chat Area */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', bgcolor: '#FFFFFF' }}>
          {currentChat ? (
            <>
              {/* Chat Header */}
              <Box sx={{ p: 2, px: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #F1F5F9' }}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Avatar
                    src={currentChat.participants?.find(p => getStrId(p._id) !== getStrId(authUser?._id))?.avatar ? (currentChat.participants.find(p => getStrId(p._id) !== getStrId(authUser?._id)).avatar.startsWith('http') ? currentChat.participants.find(p => getStrId(p._id) !== getStrId(authUser?._id)).avatar : `${urlImg}/users/${getStrId(currentChat.participants.find(p => getStrId(p._id) !== getStrId(authUser?._id))._id)}/${currentChat.participants.find(p => getStrId(p._id) !== getStrId(authUser?._id)).avatar}`) : ''}
                    sx={{ width: 44, height: 44 }}
                  />
                  <Box sx={{ minWidth: 0 }}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1E293B' }} noWrap>
                        {otherParticipant?.name} {otherParticipant?.apellido}
                      </Typography>
                      {temaTitulo(currentChat) && (
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4, bgcolor: '#EEF2FF', color: '#7495F8', px: 1, py: 0.25, borderRadius: '8px', maxWidth: '100%' }}>
                          <LocalOfferOutlinedIcon sx={{ fontSize: 13, flexShrink: 0 }} />
                          <Typography variant="caption" noWrap sx={{ fontWeight: 700 }}>
                            {temaTitulo(currentChat)}{temaCategoria(currentChat) ? ` · ${temaCategoria(currentChat)}` : ''}
                          </Typography>
                        </Box>
                      )}
                    </Stack>
                    <Typography variant="caption" sx={{ color: isOnline ? '#22C55E' : '#64748B', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ width: 6, height: 6, bgcolor: isOnline ? '#22C55E' : '#94A3B8', borderRadius: '50%' }} />
                      {isOnline ? 'Online' : 'Activo'}
                    </Typography>
                  </Box>
                </Stack>
                <Stack direction="row" spacing={1}>
                  <IconButton><InfoOutlinedIcon sx={{ color: '#94A3B8' }} /></IconButton>
                  <Tooltip title="Eliminar conversación">
                    <IconButton onClick={() => {
                      if (window.confirm('¿Eliminar conversación?')) dispatch(deleteChat(currentChat._id));
                    }}>
                      <DeleteOutlineIcon sx={{ color: '#94A3B8' }} />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Box>

              {/* Messages Area */}
              <Box sx={{ flex: 1, overflowY: 'auto', p: { xs: 2, md: 4 }, bgcolor: '#F8FAFC' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
                  <Divider sx={{ flex: 1 }} />
                  <Typography variant="caption" sx={{ px: 2, color: '#94A3B8', fontWeight: 600 }}>Hoy</Typography>
                  <Divider sx={{ flex: 1 }} />
                </Box>

                {Array.isArray(messages) && messages.map((msg, index) => {
                  const getStrId = (id) => (typeof id === 'object' && id?._id ? id._id.toString() : String(id || ''));
                  const msgSenderId = getStrId(msg.sender);
                  const myId = getStrId(authUser?._id);
                  const isMe = msgSenderId === myId;
                  
                  const sender = isMe ? authUser : currentChat.participants?.find(p => getStrId(p._id) === msgSenderId);
                  const avatar = sender?.avatar ? (sender.avatar.startsWith('http') ? sender.avatar : `${urlImg}/users/${sender._id}/${sender.avatar}`) : '';

                  return (
                    <Box key={msg._id || index} sx={{ display: 'flex', justifyContent: isMe ? 'flex-end' : 'flex-start', mb: 3 }}>
                      <Stack direction={isMe ? 'row-reverse' : 'row'} spacing={1.5} alignItems="flex-end">
                        <Avatar src={avatar} sx={{ width: 32, height: 32 }} />
                        {msg.type === 'quote' ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: isMe ? 'flex-end' : 'flex-start' }}>
                            <Button
                              variant="contained"
                              onClick={() => {
                                setSelectedQuoteId(msg.quote?._id || msg.quote);
                                setOpenQuoteDetail(true);
                              }}
                              sx={{
                                bgcolor: '#0E2465', color: 'white', borderRadius: '24px',
                                px: 4, py: 1.2, textTransform: 'none', fontWeight: 700,
                                '&:hover': { bgcolor: '#0A1A4A' },
                                boxShadow: '0 4px 12px rgba(14,36,101,0.15)'
                              }}
                            >
                              Abrir cotización
                            </Button>
                            <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.5, fontSize: '0.65rem' }}>
                              {new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </Typography>
                          </Box>
                        ) : (
                          <Box sx={{
                            maxWidth: { xs: '250px', md: '450px' }, p: 2, borderRadius: isMe ? '20px 20px 4px 20px' : '20px 20px 20px 4px',
                            bgcolor: isMe ? '#C6D4FF' : '#FFFFFF', color: '#1E293B',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                            position: 'relative'
                          }}>
                            <Typography variant="body2" sx={{ fontSize: '0.9rem', lineHeight: 1.5 }}>{msg.content}</Typography>
                            <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.5, textAlign: 'right', fontSize: '0.65rem' }}>
                              {new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </Typography>
                          </Box>
                        )}
                      </Stack>
                    </Box>
                  );
                })}
                <div ref={messagesEndRef} />
              </Box>

              {/* Input Area */}
              <Box component="form" onSubmit={handleSend} sx={{ p: 2, borderTop: '1px solid #F1F5F9' }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <IconButton sx={{ color: '#94A3B8', display: { xs: 'none', md: 'inline-flex' } }}><AttachFileIcon /></IconButton>
                  <TextField
                    fullWidth
                    placeholder="Escribe un mensaje..."
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    autoComplete="off"
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '12px', bgcolor: '#F8FAFC',
                        '& fieldset': { border: 'none' }
                      }
                    }}
                  />
                  <Button
                    variant="contained"
                    type="submit"
                    disabled={!text.trim()}
                    sx={{ bgcolor: '#E2E8F0', color: '#94A3B8', borderRadius: '10px', px: 2, textTransform: 'none', boxShadow: 'none', '&:hover': { bgcolor: '#CBD5E1' } }}
                  >
                    Enviar
                  </Button>
                  {getStrId(authUser?._id) === getStrId(currentChat?.provider) && (
                    <Button
                      variant="contained"
                      onClick={() => setOpenCotizar(true)}
                      sx={{ bgcolor: '#87B1FF', color: 'white', borderRadius: '10px', px: 2, textTransform: 'none', boxShadow: 'none', '&:hover': { bgcolor: '#7199FF' } }}
                    >
                      Cotizar
                    </Button>
                  )}
                </Stack>
              </Box>
            </>
          ) : (
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', opacity: 0.3 }}>
              <Avatar sx={{ width: 100, height: 100, bgcolor: '#F1F5F9', mb: 2 }}>
                <SendIcon sx={{ fontSize: 40, color: '#94A3B8' }} />
              </Avatar>
              <Typography variant="h6">Selecciona un mensaje</Typography>
            </Box>
          )}
        </Box>
      </Paper>
      <ModalCotizar open={openCotizar} onClose={() => setOpenCotizar(false)} currentChat={currentChat} />
      <ModalDetalleCotizar open={openQuoteDetail} onClose={() => setOpenQuoteDetail(false)} quoteId={selectedQuoteId} chatId={currentChat?._id} />
    </Container>
  );
};

export default ChatPage;
